import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useWallet } from "@/context/WalletContext";
import { ShieldCheck, Loader2, Copy, Trash2, Shield } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function trunc(a, n = 10) { return a ? `${a.slice(0, n)}…${a.slice(-4)}` : "—"; }
async function copyText(t) { try { await navigator.clipboard.writeText(t); toast.success("Copied"); } catch { /* noop */ } }

/** Admin access manager — grant/revoke admin privileges to wallet addresses.
 *  Core admins are immutable; DB-added admins get a Remove button. */
export default function AdminAccessManager() {
  const { getAuthHeaders } = useWallet();
  const [admins, setAdmins] = useState([]);
  const [form, setForm] = useState({ address: "", note: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/admin/admins`, { headers: getAuthHeaders() });
      if (res.ok) setAdmins((await res.json()).admins || []);
    } catch { /* noop */ }
  }, [getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/admin/admins`, {
        method: "POST",
        headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const j = await res.json();
      if (res.ok && j.success) {
        toast.success(`Granted admin to ${trunc(form.address, 8)}`);
        setForm({ address: "", note: "" });
        load();
      } else toast.error(j.detail || "Failed to add admin");
    } catch { toast.error("Add admin error"); }
    finally { setSaving(false); }
  };

  const remove = async (address) => {
    if (!window.confirm(`Remove admin privileges from ${trunc(address)}?`)) return;
    try {
      const res = await fetch(`${API}/api/admin/admins/${address}`, {
        method: "DELETE", headers: getAuthHeaders(),
      });
      if (res.ok) { toast.success("Admin removed"); load(); }
      else { const j = await res.json(); toast.error(j.detail || "Failed to remove"); }
    } catch { toast.error("Remove error"); }
  };

  return (
    <Card className="bg-zinc-900 border-zinc-800 mt-4" data-testid="admin-access-manager">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Shield className="w-5 h-5 text-amber-500" /> Admin Access
          <Badge variant="outline" className="ml-2">{admins.length}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="border border-zinc-800 rounded-lg p-3 mb-4 space-y-2">
          <Input placeholder="Wallet address (SP…) to grant admin" value={form.address}
            onChange={(e) => setForm({ ...form, address: e.target.value })}
            className="bg-zinc-800 border-zinc-700 font-mono text-xs"
            data-testid="admin-add-address-input" />
          <Input placeholder="Note (optional)" value={form.note}
            onChange={(e) => setForm({ ...form, note: e.target.value })}
            className="bg-zinc-800 border-zinc-700"
            data-testid="admin-add-note-input" />
          <Button className="bg-amber-600 hover:bg-amber-700" disabled={saving || !form.address}
            onClick={add} data-testid="admin-add-submit-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-1" />}
            Grant Admin
          </Button>
        </div>

        <div className="space-y-2 max-h-[40vh] overflow-y-auto">
          {admins.map((a) => (
            <div key={a.address} className="flex flex-wrap items-center justify-between gap-2 border border-zinc-800 rounded-lg p-2" data-testid={`admin-row-${a.address}`}>
              <div className="min-w-0">
                <button className="font-mono text-xs text-zinc-300 inline-flex items-center gap-1 hover:text-white"
                  onClick={() => copyText(a.address)}>
                  {trunc(a.address)} <Copy className="w-3 h-3" />
                </button>
                <div className="flex gap-1 mt-1 items-center">
                  {a.source === "core"
                    ? <Badge variant="outline" className="text-[10px] text-orange-300 border-orange-500/40">core</Badge>
                    : <Badge variant="outline" className="text-[10px] text-amber-300 border-amber-500/40">granted</Badge>}
                  {a.note && <span className="text-[10px] text-zinc-500">{a.note}</span>}
                </div>
              </div>
              {a.source === "database" ? (
                <Button size="sm" variant="outline" className="h-7 border-red-500/40 text-red-400"
                  onClick={() => remove(a.address)} data-testid={`admin-remove-${a.address}`}>
                  <Trash2 className="w-3 h-3 mr-1" /> Remove
                </Button>
              ) : (
                <span className="text-[10px] text-zinc-600">immutable</span>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
